import { FileText, X } from 'lucide-react';
import type { Order } from '@/apis/orders';
import { useAttachWarehouseFiles, useDeleteWarehouseFile } from '@/hooks/useWarehouse';
import { Dropzone } from '@/components/ui/dropzone';

const ACCEPTED_TYPES = '.pdf,.png,.jpg,.jpeg,application/pdf,image/png,image/jpeg';

interface Props {
  order: Order;
  canAct?: boolean;
}

export function FilesSection({ order, canAct = true }: Props) {
  const attachFiles = useAttachWarehouseFiles(order.id);
  const deleteFile = useDeleteWarehouseFile(order.id);
  const files = order.metadata?.warehouseFiles ?? [];

  return (
    <section>
      <div className="mb-3">
        <h2 className="text-lg font-semibold text-black">Files &amp; Attachments</h2>
      </div>
      {canAct && (
        <Dropzone
          multiple
          accept={ACCEPTED_TYPES}
          disabled={attachFiles.isPending}
          onFilesSelected={(selected) => attachFiles.mutate(selected)}
          label={attachFiles.isPending ? 'Uploading…' : 'Drop files here or browse'}
          helperText="PDF, PNG, or JPG"
          className="mb-3"
        />
      )}
      {files.length === 0 ? (
        <p className="text-sm text-lightGray">No files attached yet.</p>
      ) : (
        <div className="flex flex-wrap gap-3">
          {files.map((file) => (
            <div
              key={file.id}
              className="flex items-center gap-2 rounded-full border border-smoke bg-white px-3 py-2"
            >
              <FileText size={16} className="shrink-0 text-lightGray" />
              <a
                href={file.url}
                target="_blank"
                rel="noopener noreferrer"
                className="max-w-40 truncate text-sm font-normal text-primary-s500 hover:underline"
              >
                {file.name}
              </a>
              {canAct && (
                <button
                  type="button"
                  onClick={() => deleteFile.mutate(file.id)}
                  disabled={deleteFile.isPending}
                  className="text-silver hover:text-danger disabled:opacity-50"
                  aria-label="Remove file"
                >
                  <X size={13} />
                </button>
              )}
            </div>
          ))}
        </div>
      )}
    </section>
  );
}
